// The browser side of CustomForms::Conditions: both must apply the same rules

export const OPERATORS = ['is', 'is_not', 'answered', 'not_answered', 'greater_than', 'less_than']
export const VALUELESS = ['answered', 'not_answered']
export const ANSWERED_VALUE = '__answered__'

export const hasCondition = (condition) => !!(condition && condition.field_id && condition.operator)

const toNumber = (value) => {
  const number = parseFloat(String(value).replace(',', '.'))
  return Number.isNaN(number) ? null : number
}

// "5" and "5.0" are the same number
const matchesAny = (value, expected) => {
  const number = toNumber(value)
  return expected.some((candidate) => candidate === value || (number !== null && toNumber(candidate) === number))
}

export const conditionMet = (condition, values) => {
  if (!hasCondition(condition)) { return true }

  const expected = (condition.values || []).map(String)
  // A condition still waiting for its values hides nothing
  if (expected.length === 0 && !VALUELESS.includes(condition.operator)) { return true }
  const answered = values.filter((value) => value !== null && value !== undefined && String(value).trim() !== '').map(String)

  switch (condition.operator) {
    case 'is':           return answered.some((value) => matchesAny(value, expected))
    case 'is_not':       return !answered.some((value) => matchesAny(value, expected))
    case 'answered':     return answered.length > 0
    case 'not_answered': return answered.length === 0
    case 'greater_than':
    case 'less_than': {
      const answer = toNumber(answered[0])
      const limit = toNumber(expected[0])
      if (answer === null || limit === null) { return false }
      return condition.operator === 'greater_than' ? answer > limit : answer < limit
    }
    default:             return true
  }
}

// A hidden section hides its fields, and a hidden source hides what follows it
export const visibleKeys = (items) => {
  const byKey = Object.fromEntries(items.map((item) => [item.key, item]))
  const byFieldId = {}
  items.forEach((item) => { (byFieldId[item.fieldId] = byFieldId[item.fieldId] || []).push(item) })
  const memo = {}

  const sourceFor = (item, fieldId) => {
    const candidates = byFieldId[fieldId] || []
    // Same copy of a repeated section first, as the server's Evaluator picks it
    return candidates.find((candidate) => item.sectionKey !== null && candidate.sectionKey === item.sectionKey) ||
      candidates.find((candidate) => !candidate.inCopy) ||
      candidates[0]
  }

  const visible = (item, seen = new Set()) => {
    if (memo[item.key] !== undefined) { return memo[item.key] }
    if (seen.has(item.key)) { return true }
    seen.add(item.key)

    let result = true
    const section = item.sectionKey !== null && byKey[item.sectionKey]
    if (section && !visible(section, seen)) {
      result = false
    } else if (hasCondition(item.condition)) {
      const source = sourceFor(item, item.condition.field_id)
      if (source) {
        result = visible(source, seen) && conditionMet(item.condition, source.values)
      }
    }
    memo[item.key] = result
    return result
  }

  return new Set(items.filter((item) => visible(item)).map((item) => item.key))
}
